import type { ReactNode } from "react";

import type { UsageProviderKind } from "@cmmd-forge/contracts";
import type {
  DailyTotals,
  DriverTotals,
  EnvironmentTotals,
  HourlyTotals,
  KindTotals,
  ModelTotals,
  ProviderTotals,
} from "@cmmd-forge/shared/usageMerge";
import {
  formatDayShort,
  formatHourShort,
  formatTokens,
  formatUsd,
} from "@cmmd-forge/shared/usageFormat";

import { Toggle, ToggleGroup } from "../ui/toggle-group";
import {
  UsageDriverTable,
  UsageEnvironmentTable,
  UsageKindTable,
  UsageModelTable,
  UsageProviderTable,
} from "./UsageBreakdownTables";
import type { UsageChartMetric } from "./UsageProviderChart";
import {
  isBreakdownMode,
  usageBreakdownChoices,
  type UsageBreakdownMode,
} from "./usageBreakdownModes";
import { PROVIDER_PRESENTATION } from "./usageProviders";

export type { UsageBreakdownMode } from "./usageBreakdownModes";

interface BreakdownTableProps {
  readonly environments: readonly EnvironmentTotals[];
  readonly labelForEnvironment: (row: EnvironmentTotals) => string;
  readonly providers: readonly ProviderTotals[];
  readonly models: readonly ModelTotals[];
  readonly drivers: readonly DriverTotals[];
  readonly kinds: readonly KindTotals[];
  readonly periods: readonly (DailyTotals | HourlyTotals)[];
  readonly activeProviders: readonly UsageProviderKind[];
  readonly resolution: "day" | "hour";
  readonly timeZone: string;
}

/** The table each mode shows, so the component itself has no branch per mode. */
const BREAKDOWN_TABLES: Record<UsageBreakdownMode, (props: BreakdownTableProps) => ReactNode> = {
  environment: (props) => (
    <UsageEnvironmentTable environments={props.environments} labelFor={props.labelForEnvironment} />
  ),
  provider: (props) => (
    <UsageProviderTable providers={props.providers} activeProviders={props.activeProviders} />
  ),
  model: (props) => <UsageModelTable models={props.models} />,
  driver: (props) => <UsageDriverTable drivers={props.drivers} />,
  kind: (props) => <UsageKindTable kinds={props.kinds} />,
  time: (props) => (
    <UsagePeriodTable
      periods={props.periods}
      activeProviders={props.activeProviders}
      resolution={props.resolution}
      timeZone={props.timeZone}
    />
  ),
};

/**
 * Where the total came from: by environment, harness, model, period, driver,
 * or what each row is a row of.
 */
export function UsageBreakdown({
  mode,
  onModeChange,
  metric,
  environments,
  labelForEnvironment,
  providers,
  models,
  drivers,
  kinds,
  periods,
  activeProviders,
  resolution,
  timeZone,
}: {
  readonly mode: UsageBreakdownMode;
  readonly onModeChange: (mode: UsageBreakdownMode) => void;
  readonly metric: UsageChartMetric;
  readonly environments: readonly EnvironmentTotals[];
  readonly labelForEnvironment: (row: EnvironmentTotals) => string;
  readonly providers: readonly ProviderTotals[];
  readonly models: readonly ModelTotals[];
  readonly drivers: readonly DriverTotals[];
  readonly kinds: readonly KindTotals[];
  readonly periods: readonly (DailyTotals | HourlyTotals)[];
  readonly activeProviders: readonly UsageProviderKind[];
  readonly resolution: "day" | "hour";
  readonly timeZone: string;
}) {
  // Ranked by whatever the toggle is showing, so the first row is always the
  // one the reader came for. Cost order is already the merge's own order.
  const orderedModels =
    metric === "tokens"
      ? models.toSorted(
          (left, right) => right.totalTokens - left.totalTokens || right.costUsd - left.costUsd,
        )
      : models;

  const { mode: effectiveMode, options } = usageBreakdownChoices(mode, kinds, resolution);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-foreground">Breakdown</h2>
        <ToggleGroup
          aria-label="Usage breakdown"
          variant="segmented"
          value={[effectiveMode]}
          onValueChange={(next) => {
            const value = next[0];
            if (isBreakdownMode(value)) onModeChange(value);
          }}
        >
          {options.map((option) => (
            <Toggle key={option.value} value={option.value}>
              {option.label}
            </Toggle>
          ))}
        </ToggleGroup>
      </div>

      {BREAKDOWN_TABLES[effectiveMode]({
        environments,
        labelForEnvironment,
        providers,
        models: orderedModels,
        drivers,
        kinds,
        periods,
        activeProviders,
        resolution,
        timeZone,
      })}
    </section>
  );
}

function UsagePeriodTable({
  periods,
  activeProviders,
  resolution,
  timeZone,
}: {
  readonly periods: readonly (DailyTotals | HourlyTotals)[];
  readonly activeProviders: readonly UsageProviderKind[];
  readonly resolution: "day" | "hour";
  readonly timeZone: string;
}) {
  // The period column keeps 2/5; the remaining 3/5 divides evenly across every
  // lane plus the two total columns, so adding a lane narrows the row rather
  // than widening the table.
  const valueColumnWidth = `${60 / (activeProviders.length + 2)}%`;

  return (
    // One column per lane plus two totals does not fit 320px. The table scrolls
    // inside its own box so the page never scrolls sideways.
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[32rem] table-fixed text-sm">
        <colgroup>
          <col className="w-2/5" />
          {activeProviders.map((provider) => (
            <col key={provider} style={{ width: valueColumnWidth }} />
          ))}
          <col style={{ width: valueColumnWidth }} />
          <col style={{ width: valueColumnWidth }} />
        </colgroup>
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="py-2 font-normal">{resolution === "hour" ? "Hour" : "Day"}</th>
            {activeProviders.map((provider) => (
              <th key={provider} className="py-2 text-right font-normal">
                {PROVIDER_PRESENTATION[provider].label}
              </th>
            ))}
            <th className="py-2 text-right font-normal">Total</th>
            <th className="py-2 text-right font-normal">Tokens</th>
          </tr>
        </thead>
        <tbody>
          {periods.length === 0 ? (
            <tr>
              <td
                colSpan={activeProviders.length + 3}
                className="py-6 text-center text-muted-foreground"
              >
                No activity in this window.
              </td>
            </tr>
          ) : (
            periods.map((period) => (
              <tr
                key={"hourStart" in period ? period.hourStart : period.day}
                className="border-b border-border/50 transition-colors hover:bg-muted/50"
              >
                <td className="py-2 text-foreground">
                  {"hourStart" in period
                    ? formatHourShort(period.hourStart, timeZone)
                    : formatDayShort(period.day)}
                </td>
                {activeProviders.map((provider) => (
                  <td key={provider} className="py-2 text-right text-muted-foreground tabular-nums">
                    {formatUsd(period.byProvider.get(provider)?.costUsd ?? 0)}
                  </td>
                ))}
                <td className="py-2 text-right text-foreground tabular-nums">
                  {formatUsd(period.costUsd)}
                </td>
                <td className="py-2 text-right text-muted-foreground tabular-nums">
                  {formatTokens(period.totalTokens)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
